import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, DollarSign, Layers, Download, Copy } from 'lucide-react';
import { maquetaService, editionService, publicidadService } from '../../services/api';
import PaginaCanvas from './PaginaCanvas';
import PlantillaModal from './PlantillaModal';

/**
 * MaquetaTab — tab de "Maquetación impresa" dentro del editor de ediciones.
 *
 * Layout (3 columnas):
 *   ┌─────────────┬──────────────────────────┬───────────────┐
 *   │ SIDEBAR IZ. │        CANVAS            │ NAVEGADOR PÁG │
 *   │ Artículos   │  Grid 6col × N filas     │ [1] [2] [3]…  │
 *   │ Pautas      │  con bloques colocados   │ + Nueva pág   │
 *   └─────────────┴──────────────────────────┴───────────────┘
 *
 * Se arrastran artículos/pautas desde la sidebar hacia el canvas. Los
 * bloques ya en el canvas se pueden mover y redimensionar. Los cambios
 * se persisten inmediatamente (sin botón "Guardar" — WYSIWYG real).
 */
// Un bloque cuenta como "lleno" cuando ya tiene contenido. Los bloques de
// artículo y de pauta se llenan con una referencia; los elemento_fijo y los
// dinámicos guardan su contenido en elementoData. Antes solo se miraban los
// dos primeros, así que una página completa a base de elementos fijos nunca
// se marcaba como terminada.
const bloqueLleno = (b) => Boolean(
  b.articuloId ||
  b.pautaId ||
  (typeof b.elementoData === 'string' &&
   b.elementoData.trim() !== '' &&
   b.elementoData.trim() !== 'null')
);

export default function MaquetaTab({ edicionId }) {
  const [paginas, setPaginas] = useState([]);
  const [paginaActivaId, setPaginaActivaId] = useState(null);
  const [articulos, setArticulos] = useState([]);
  const [pautas, setPautas] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [modalPlantilla, setModalPlantilla] = useState(null); // null | {} | plantilla
  // Modo que se está exportando ('rgb' | 'print' | 'pliego') o null. Generar el
  // PDF de una edición completa toma varios segundos: sin este indicador el
  // editor vuelve a hacer clic creyendo que no pasó nada.
  const [exportando, setExportando] = useState(null);
  // Medidas del bloque bajo el cursor. Se escriben DIRECTO al DOM con una
  // referencia, sin pasar por estado de React: al mover el mouse no se repinta
  // ni un componente, y como la caja tiene alto fijo y no envuelve, tampoco
  // puede reacomodar el layout ni provocar el parpadeo de antes.
  // El alto del editor se medía con calc(100vh - 220px). Esos 220 son una
  // estimación fija del encabezado y las pestañas: si la franja real mide
  // menos, sobra una banda muerta abajo, y si mide más, aparece scroll. Se
  // mide la posición real del contenedor y se recalcula al cambiar el tamaño.
  const zonaRef = useRef(null);
  const [altoZona, setAltoZona] = useState(null);
  useEffect(() => {
    const medir = () => {
      const el = zonaRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setAltoZona(Math.max(420, Math.round(window.innerHeight - top - 12)));
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  const medidasRef = useRef(null);
  const mostrarMedidas = (texto) => {
    const el = medidasRef.current;
    if (!el) return;
    el.textContent = texto || 'Pasa el cursor por un bloque';
    el.style.color = texto ? '#1B4332' : '#8a8a85';
    el.style.fontStyle = texto ? 'normal' : 'italic';
  };
  const [soloSinUbicar, setSoloSinUbicar] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const paginaActiva = paginas.find(p => p.id === paginaActivaId);

  // Índice: articuloId → { pagina, bloque }. Se recomputa en cada cambio
  // de páginas. Sirve para (a) el badge en cada artículo de la sidebar,
  // (b) filtro "sin ubicar". Un artículo puede aparecer en varias páginas
  // (hero portada + cuerpo completo interior); guardamos todas.
  const ubicaciones = new Map(); // articuloId → { numeroPagina (primera), paginas: [n1, n2…] }
  for (const p of paginas) {
    for (const b of p.bloques) {
      if (b.articuloId) {
        const prev = ubicaciones.get(b.articuloId);
        if (prev) {
          if (!prev.paginas.includes(p.numeroPagina)) prev.paginas.push(p.numeroPagina);
        } else {
          ubicaciones.set(b.articuloId, { numeroPagina: p.numeroPagina, bloqueId: b.id, paginas: [p.numeroPagina] });
        }
      }
    }
  }
  // Normalizar quitando acentos y minúsculas para búsqueda tolerante
  const q = busqueda.trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const articulosVisibles = articulos.filter(a => {
    if (soloSinUbicar && ubicaciones.has(a.id)) return false;
    if (!q) return true;
    const campos = [
      a.title || a.titulo || '',
      a.subtitle || a.subtitulo || '',
      a.summary || a.resumen || '',
      a.categoryName || a.categoria || '',
      // Autores puede venir como array u objeto único
      Array.isArray(a.authors) ? a.authors.map(x => x.name || x.nombre || '').join(' ')
        : (a.autor || a.author || ''),
    ];
    const heno = campos.join(' ').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return heno.includes(q);
  });

  const cargar = useCallback(async () => {
    if (!edicionId) return;
    try {
      const [pgs, edicion, pts, plts] = await Promise.all([
        maquetaService.listarPaginas(edicionId),
        editionService.getById(edicionId).catch(() => null),
        // Pautas contratadas específicamente para esta edición (activas y
        // dentro del período de vigencia). Se cargan sin filtro de posición
        // porque el editor puede colocarlas donde considere en la maqueta.
        publicidadService.getPautasActivas(null, edicionId).catch(() => []),
        maquetaService.listarPlantillas().catch(() => []),
      ]);
      setPaginas(pgs);
      setArticulos(edicion?.articles || []);
      setPautas(Array.isArray(pts) ? pts : (pts?.data ?? pts?.items ?? []));
      setPlantillas(plts);
      if (pgs.length > 0 && !pgs.find(p => p.id === paginaActivaId)) {
        setPaginaActivaId(pgs[0].id);
      }
    } catch (err) {
      console.error('Error cargando maqueta:', err);
    }
  }, [edicionId, paginaActivaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Nota: las páginas se precrean/eliminan al guardar la edición cuando
  // el editor cambia total_paginas. Este componente NO crea/elimina
  // páginas — solo trabaja con las que ya existen.

  const aplicarPlantilla = async (paginaId, plantillaId) => {
    try {
      const p = await maquetaService.aplicarPlantilla(paginaId, plantillaId);
      setPaginas(prev => prev.map(x => x.id === paginaId ? p : x));
      window.dispatchEvent(new CustomEvent('app:toast', { detail: 'Plantilla aplicada' }));
    } catch (err) {
      alert('Error aplicando plantilla: ' + err.message);
    }
  };

  // Cambiar columnas/filas de la página. Actualiza el estado local al instante
  // para que el input responda, y guarda al backend con debounce de 500ms.
  const timersDim = useRef({});
  const cambiarDimensionesPagina = (paginaId, campo, valor) => {
    setPaginas(prev => prev.map(p => p.id === paginaId ? { ...p, [campo]: valor } : p));
    if (timersDim.current[paginaId]) clearTimeout(timersDim.current[paginaId]);
    timersDim.current[paginaId] = setTimeout(async () => {
      try {
        const pagActual = (await maquetaService.obtenerPagina(paginaId).catch(() => null))
          ?? paginas.find(x => x.id === paginaId);
        if (!pagActual) return;
        // Enviar el DTO completo con el nuevo valor
        const dto = {
          numeroPagina: pagActual.numeroPagina,
          seccion:      pagActual.seccion ?? null,
          plantillaId:  pagActual.plantillaId ?? null,
          columnas:     campo === 'columnas' ? valor : pagActual.columnas,
          filas:        campo === 'filas'    ? valor : pagActual.filas,
          notas:        pagActual.notas ?? null,
        };
        await maquetaService.actualizarPagina(paginaId, dto);
        window.dispatchEvent(new CustomEvent('app:toast', { detail: 'Página actualizada' }));
      } catch (err) {
        alert('No se pudo guardar el cambio: ' + err.message);
        await cargar();
      }
    }, 500);
  };

  const exportarPdf = async (modo) => {
    // El endpoint exige autenticación. window.open abre una pestaña sin la
    // cabecera Authorization, por eso devolvía 401: hay que traer el archivo
    // por fetch con el token y abrirlo desde un blob local.
    try {
      setExportando(modo);
      const blob = await maquetaService.descargarPdf(edicionId, modo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `edicion-${edicionId}-${modo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Se libera con retraso: si se revoca de inmediato, algunos navegadores
      // cancelan la descarga que acaba de empezar.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('No se pudo generar el PDF: ' + err.message);
    } finally {
      setExportando(null);
    }
  };

  if (!edicionId) {
    return <div style={{ padding:20, color:'#888' }}>Guarda la edición primero para maquetar sus páginas.</div>;
  }

  return (
    <div
      ref={zonaRef}
      style={{ display:'flex', gap:12, height: altoZona ? `${altoZona}px` : 'calc(100vh - 220px)' }}
    >
      {/* ─── Sidebar izquierda: artículos y pautas para arrastrar ─── */}
      <aside style={{
        width:260, background:'#fafafa', border:'1px solid #e5e7eb',
        borderRadius:8, overflow:'hidden', display:'flex', flexDirection:'column',
      }}>
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #e5e7eb', fontWeight:600, fontSize:13, color:'#1B4332', display:'flex', alignItems:'center', gap:6 }}>
          <FileText size={14} />
          <span style={{ flex:1 }}>Artículos ({articulosVisibles.length}/{articulos.length})</span>
          <label style={{ fontSize:11, fontWeight:400, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:4 }}>
            <input type="checkbox" checked={soloSinUbicar} onChange={(e) => setSoloSinUbicar(e.target.checked)} />
            Sin ubicar
          </label>
        </div>
        <div style={{ padding:'8px 8px 0', borderBottom:'1px solid #f0f0f0' }}>
          <div style={{ position:'relative' }}>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por título, autor, categoría…"
              style={{
                width:'100%', padding:'6px 24px 6px 10px', fontSize:12,
                border:'1px solid #d1d5db', borderRadius:4, outline:'none',
                boxSizing:'border-box',
              }}
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda('')}
                title="Limpiar"
                style={{
                  position:'absolute', right:4, top:'50%', transform:'translateY(-50%)',
                  background:'transparent', border:'none', cursor:'pointer',
                  fontSize:14, color:'#9ca3af', padding:'2px 6px', lineHeight:1,
                }}
              >×</button>
            )}
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:8 }}>
          {articulosVisibles.length === 0 && (
            <div style={{ color:'#888', fontSize:12, padding:12, textAlign:'center' }}>
              {q
                ? `Sin resultados para "${busqueda}"`
                : soloSinUbicar
                  ? '¡Todos los artículos ya están ubicados! 🎉'
                  : 'Asigna artículos a esta edición para maquetarlos.'}
            </div>
          )}
          {articulosVisibles.map(a => {
            const ubi = ubicaciones.get(a.id);
            return (
              <div
                key={a.id}
                draggable={true}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', JSON.stringify({
                    tipo: 'articulo', articuloId: a.id, titulo: a.title || a.titulo,
                  }));
                }}
                style={{
                  background: ubi ? '#f0fdf4' : '#fff',
                  border: ubi ? '1px solid #86efac' : '1px solid #ddd',
                  borderRadius:4, padding:8, marginBottom:6,
                  cursor: 'grab',
                  fontSize:12,
                }}
                title={ubi ? `Ya está en pág. ${ubi.numeroPagina} — puedes arrastrarlo a otra página (p.ej. continuación).` : 'Arrastra al canvas'}
              >
                <div style={{ display:'flex', gap:4, alignItems:'flex-start' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, color:'#1B4332', marginBottom:2 }}>
                      {a.title || a.titulo || 'Sin título'}
                    </div>
                    {(a.resumen || a.excerpt) && (
                      <div style={{ color:'#666', fontSize:11, overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                        {a.resumen || a.excerpt}
                      </div>
                    )}
                  </div>
                  {ubi && (
                    <span style={{
                      background:'#16a34a', color:'#fff',
                      fontSize:10, fontWeight:700, padding:'2px 6px',
                      borderRadius:10, whiteSpace:'nowrap', flexShrink:0,
                    }}>Pág. {ubi.paginas.join(', ')}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding:'10px 12px', borderTop:'1px solid #e5e7eb', borderBottom:'1px solid #e5e7eb', fontWeight:600, fontSize:13, color:'#1B4332' }}>
          <DollarSign size={14} style={{ verticalAlign:'middle', marginRight:6 }} />
          Pautas ({pautas.length})
        </div>
        <div style={{ maxHeight:180, overflowY:'auto', padding:8 }}>
          {pautas.length === 0 && (
            <div style={{ fontSize:11, color:'#999', textAlign:'center', padding:'12px 8px', fontStyle:'italic' }}>
              No hay pautas contratadas para esta edición
            </div>
          )}
          {pautas.map(p => (
            <div
              key={p.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', JSON.stringify({
                  tipo: 'pauta',
                  pautaId: p.id,
                  titulo: p.anunciante || p.nombreMaterial || `Pauta #${p.id}`,
                }));
              }}
              style={{
                background:'#fff', border:'1px solid #ddd', borderRadius:4,
                padding:8, marginBottom:6, cursor:'grab', fontSize:12,
                display:'flex', gap:8, alignItems:'center',
              }}
              title={`${p.anunciante || '—'} · ${p.formatoNombre || ''}`}
            >
              {p.tipoArchivo === 'imagen' && p.urlStorage ? (
                <img src={p.urlStorage} alt="" style={{
                  width:36, height:24, objectFit:'contain', flexShrink:0,
                  border:'1px solid #eee', background:'#fafafa',
                }} />
              ) : (
                <div style={{
                  width:36, height:24, background:'#e5e7eb', borderRadius:2, flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:9, color:'#666',
                }}>{p.tipoArchivo?.[0]?.toUpperCase() || '?'}</div>
              )}
              <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                <div style={{ fontWeight:600, color:'#1e40af', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {p.anunciante || `Pauta #${p.id}`}
                </div>
                <div style={{ fontSize:10, color:'#666', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {p.formatoNombre || p.nombreMaterial || ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ─── Canvas central ────────────────────────────────────────── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8, minWidth:0 }}>
        {/* Toolbar */}
        <div style={{
          display:'flex', gap:8, alignItems:'center', padding:'8px 12px',
          background:'#fff', border:'1px solid #e5e7eb', borderRadius:8,
        }}>
          {paginaActiva ? (
            <>
              <strong style={{ fontSize:13, color:'#1B4332' }}>Página {paginaActiva.numeroPagina}</strong>
              <span style={{ color:'#888', fontSize:12 }}>·</span>
              <label style={{ fontSize:12, color:'#555', display:'flex', alignItems:'center', gap:4 }} title="Columnas del grid de esta página">
                <input
                  type="number" min={1} max={12}
                  value={paginaActiva.columnas}
                  onChange={(e) => cambiarDimensionesPagina(paginaActiva.id, 'columnas', parseInt(e.target.value, 10) || 6)}
                  style={{ width:44, padding:'2px 4px', fontSize:12, border:'1px solid #ddd', borderRadius:4, textAlign:'center' }}
                />
                col
              </label>
              <span style={{ color:'#bbb', fontSize:12 }}>×</span>
              <label style={{ fontSize:12, color:'#555', display:'flex', alignItems:'center', gap:4 }} title="Filas del grid de esta página (a mayor número, más precisión vertical)">
                <input
                  type="number" min={4} max={40}
                  value={paginaActiva.filas}
                  onChange={(e) => cambiarDimensionesPagina(paginaActiva.id, 'filas', parseInt(e.target.value, 10) || 12)}
                  style={{ width:44, padding:'2px 4px', fontSize:12, border:'1px solid #ddd', borderRadius:4, textAlign:'center' }}
                />
                filas
              </label>
              <select
                value={paginaActiva.plantillaId ?? ''}
                onChange={(e) => e.target.value && aplicarPlantilla(paginaActiva.id, parseInt(e.target.value))}
                style={{ fontSize:12, padding:'4px 8px', border:'1px solid #ddd', borderRadius:4 }}
                title="Aplicar plantilla — reemplaza los bloques"
              >
                <option value="">— Aplicar plantilla —</option>
                {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              {/* Medida del bloque bajo el cursor: la única parte que varía.
                  El tamaño de la página está en su ficha, en la columna
                  izquierda del lienzo. */}
              <div
                ref={medidasRef}
                style={{
                  marginLeft:12, flex:'1 1 220px', minWidth:0,
                  height:30, display:'flex', alignItems:'center',
                  padding:'0 12px', borderRadius:4,
                  background:'#f4f3ef', border:'1px solid #ddd9cf',
                  fontSize:12, whiteSpace:'nowrap', overflow:'hidden',
                  textOverflow:'ellipsis', fontVariantNumeric:'tabular-nums',
                  color:'#8a8a85', fontStyle:'italic',
                }}
              >
                Pasa el cursor por un bloque
              </div>
            </>
          ) : (
            <span style={{ color:'#888', fontSize:13 }}>Selecciona una página del panel derecho.</span>
          )}
          <div style={{ marginLeft:'auto', display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
            <button onClick={() => setModalPlantilla({})} style={btnGhost} title="Guardar plantilla nueva">
              <Copy size={14} /> Plantillas
            </button>
            <button onClick={() => exportarPdf('rgb')} style={btnPrimary} disabled={!!exportando}
                    title="PDF para imprimir en casa">
              <Download size={14} /> {exportando === 'rgb' ? 'Generando…' : 'PDF (RGB)'}
            </button>
            <button onClick={() => exportarPdf('pliego')} style={btnPrimary} disabled={!!exportando}
                    title="Dos páginas por cara, en el orden en que quedan al doblar. Es el archivo que recibe la litografía.">
              <Download size={14} /> {exportando === 'pliego' ? 'Generando…' : 'PDF (Pliegos)'}
            </button>
            <button onClick={() => exportarPdf('print')} style={btnPrimary} disabled={!!exportando}
                    title="PDF con marcas de corte y sangrado">
              <Download size={14} /> {exportando === 'print' ? 'Generando…' : 'PDF (Imprenta)'}
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div style={{ flex:1, overflow:'auto', background:'#e5e7eb', padding:20, borderRadius:8 }}>
          {paginaActiva ? (
            <PaginaCanvas
              pagina={paginaActiva}
              onCambio={cargar}
              onFocoBloque={mostrarMedidas}
            />
          ) : (
            <div style={{
              display:'flex', alignItems:'center', justifyContent:'center',
              height:'100%', color:'#888', fontSize:14,
            }}>
              Selecciona o crea una página para maquetar.
            </div>
          )}
        </div>
      </div>

      {/* ─── Navegador de páginas ──────────────────────────────────── */}
      <aside style={{
        width:140, background:'#fafafa', border:'1px solid #e5e7eb',
        borderRadius:8, overflow:'hidden', display:'flex', flexDirection:'column',
      }}>
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #e5e7eb', fontWeight:600, fontSize:13, color:'#1B4332' }}>
          <Layers size={14} style={{ verticalAlign:'middle', marginRight:6 }} />
          Páginas ({paginas.length})
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:8 }}>
          {paginas.map(p => {
            const conBloques = p.bloques.filter(bloqueLleno).length;
            const totalBloques = p.bloques.length;
            const completa = totalBloques > 0 && conBloques === totalBloques;
            return (
              <button
                key={p.id}
                onClick={() => setPaginaActivaId(p.id)}
                style={{
                  width:'100%', padding:8, marginBottom:6,
                  background: p.id === paginaActivaId ? '#1B4332' : '#fff',
                  color: p.id === paginaActivaId ? '#fff' : '#222',
                  border:'1px solid #ddd', borderRadius:4,
                  cursor:'pointer', fontSize:12, fontWeight:600, textAlign:'center',
                  position:'relative',
                }}
              >
                Pág. {p.numeroPagina}
                <div style={{ fontSize:10, fontWeight:400, marginTop:2, opacity:0.8 }}>
                  {totalBloques === 0
                    ? 'vacía'
                    : `${conBloques}/${totalBloques} llenos`}
                </div>
                {completa && (
                  <span style={{
                    position:'absolute', top:4, right:4,
                    background:'#16a34a', color:'#fff',
                    borderRadius:'50%', width:14, height:14,
                    fontSize:10, display:'flex', alignItems:'center', justifyContent:'center',
                  }}>✓</span>
                )}
              </button>
            );
          })}
          {paginas.length === 0 && (
            <div style={{ fontSize:11, color:'#888', textAlign:'center', padding:12, lineHeight:1.4 }}>
              Guarda la edición con un total de páginas para que aparezcan aquí.
            </div>
          )}
        </div>
        <div style={{ padding:'8px 10px', borderTop:'1px solid #e5e7eb', fontSize:10, color:'#666', lineHeight:1.4 }}>
          El total de páginas se define en el tab <strong>Contenido</strong>. Cámbialo allí para agregar o quitar hojas.
        </div>
      </aside>

      {/* Modal de plantillas */}
      {modalPlantilla !== null && (
        <PlantillaModal
          plantillasExistentes={plantillas}
          onClose={() => setModalPlantilla(null)}
          onGuardado={async () => {
            const plts = await maquetaService.listarPlantillas();
            setPlantillas(plts);
            setModalPlantilla(null);
          }}
          paginaActual={paginaActiva}
        />
      )}
    </div>
  );
}

const btnGhost = {
  background:'transparent', border:'1px solid #ddd', borderRadius:4,
  padding:'4px 10px', cursor:'pointer', fontSize:12, color:'#555',
  display:'inline-flex', alignItems:'center', gap:6,
};
const btnPrimary = {
  background:'#1B4332', color:'#fff', border:'none', borderRadius:4,
  padding:'4px 12px', cursor:'pointer', fontSize:12, fontWeight:600,
  display:'inline-flex', alignItems:'center', gap:6,
};
